import React from 'react';

import {
  Box,
  ButtonBase,
  MenuItem,
  Card,
  Popover,
  Typography,
  Button,
  styled,
} from '@mui/material';

import { CalendarToday } from '@mui/icons-material';

import type { ReactDatePickerProps } from 'react-datepicker';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { usePresetOptions } from './helpers/usePresetOptions';

import type { DateRangeValue, HandleApply } from './helpers/types';
import { formatDateForLocale } from '../../../utils/hooks/useDateFormat';

type PassedReactPickerProps = Omit<
  ReactDatePickerProps,
  'onChange' | 'value' | 'selectRange' | 'startDate' | 'endDate'
>;

type DateRangePickerProps = {
  initialValue?: DateRangeValue;
  onApply?: HandleApply;
  dateFormatter?: (date: Date) => string;
} & PassedReactPickerProps;

export const DateRangePicker = ({
  dateFormatter = formatDate,
  initialValue,
  onApply,
  ...props
}: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const elRef = React.useRef<HTMLButtonElement>(null);

  const [startDateSelection, setStartDateSelection] =
    React.useState<Date | null>(initialValue?.startDate ?? null);

  const [endDateSelection, setEndDateSelection] = React.useState<Date | null>(
    initialValue?.endDate ?? null
  );

  const formattedStartDate = startDateSelection
    ? dateFormatter(startDateSelection)
    : null;

  const formattedEndDate = endDateSelection
    ? dateFormatter(endDateSelection)
    : null;

  const isInitialValSetRef = React.useRef(false);
  React.useEffect(() => {
    if (initialValue && !isInitialValSetRef.current) {
      // we have not yet consumed the initialValue, set it in to our selection state
      if (initialValue.startDate) {
        setStartDateSelection(initialValue.startDate);
        isInitialValSetRef.current = true;
      }

      if (initialValue.endDate) {
        setEndDateSelection(initialValue.endDate);
        isInitialValSetRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialValue)]);

  const handleDatePickerChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDateSelection(start);
    setEndDateSelection(end);
  };

  const handlePresetClick = (dates: DateRangeValue) => {
    setStartDateSelection(dates.startDate);
    setEndDateSelection(dates.endDate);
  };

  const handleApplyClick = () => {
    onApply?.({ startDate: startDateSelection, endDate: endDateSelection });
    setIsOpen(false);
  };

  const presetOptions = usePresetOptions();

  const isRangeSelected =
    startDateSelection != null && endDateSelection != null;

  return (
    <>
      <DateButton ref={elRef} onClick={() => setIsOpen(true)}>
        {isRangeSelected ? (
          <Box>
            <Typography fontSize="1rem" noWrap color="primary">
              {formattedStartDate} - {formattedEndDate}
            </Typography>
          </Box>
        ) : (
          <Typography fontSize="1rem" noWrap color="primary">
            Select Dates
          </Typography>
        )}

        <CalendarToday color="primary" sx={{ fontSize: '1.2rem' }} />
      </DateButton>

      <Popover
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorEl={elRef.current}
      >
        <Card sx={{ padding: '8px' }}>
          <Box display="flex" gap="8px">
            <DatePickerWrapper>
              <DatePicker
                disabledKeyboardNavigation
                inline
                selected={startDateSelection}
                {...props}
                selectsRange
                startDate={startDateSelection}
                endDate={endDateSelection}
                onChange={handleDatePickerChange}
              />
            </DatePickerWrapper>

            <Box sx={{ width: '150px' }}>
              <PresetOptionsList>
                {presetOptions.map((preset) => (
                  <MenuItem
                    key={preset.label}
                    color="primary"
                    dense
                    sx={{ width: '100%' }}
                    onClick={() => handlePresetClick(preset.value)}
                  >
                    {preset.label}
                  </MenuItem>
                ))}
              </PresetOptionsList>

              <Button
                variant="contained"
                color="primary"
                sx={{ minWidth: 'unset', width: '100%', marginTop: '8px' }}
                onClick={handleApplyClick}
                disabled={!isRangeSelected}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Card>
      </Popover>
    </>
  );
};

const formatDate = formatDateForLocale;

const PresetOptionsList = styled('ul')`
  border: 1px solid ${(props) => props.theme.palette.border.soft};
  border-radius: 4px;

  display: flex;
  flex-direction: column;
  align-items: center;

  margin: 0;
  padding: 0;
`;

const DateButton = styled(ButtonBase)`
  width: fit-content;
  height: fit-content;
  padding: 8px 8px;

  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;

  border: 1px solid ${(props) => props.theme.palette.border.soft};
  border-radius: 4px;
`;

const DatePickerWrapper = styled(Box)`
  border: 1px solid ${(props) => props.theme.palette.border.soft};

  border-radius: 4px;

  & .react-datepicker {
    border: none;
  }

  & .react-datepicker__month-container {
    background-color: ${(props) => props.theme.palette.background.paper};
    color: ${(props) => props.theme.palette.background.contrastText};
  }

  & .react-datepicker__navigation-icon::before {
    border-color: ${(props) => props.theme.palette.primary.main};
  }

  & .react-datepicker__header {
    background-color: ${(props) => props.theme.palette.background.paper};
    color: ${(props) => props.theme.palette.background.contrastText};
  }

  & .react-datepicker__current-month {
    color: ${(props) => props.theme.palette.background.contrastText};
  }

  & .react-datepicker__day-name,
  .react-datepicker__day {
    color: ${(props) => props.theme.palette.background.contrastText};
    font-size: 0.8rem;
  }

  & .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--selected,
  .react-datepicker__day:hover,
  .react-datepicker__day--keyboard-selected {
    background: ${(props) => props.theme.palette.primary.main} !important;
    color: ${(props) => props.theme.palette.primary.contrastText};
  }
`;
